import * as React from "react"

import { cva } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"

const inputVariants = cva([
  "h-control w-full rounded-md px-2 py-1.5",
  "text-sm text-coval-text placeholder:text-coval-text-muted",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "disabled:cursor-default read-only:cursor-default",
  "border border-surface-1",
  "focus:border-coval-border-active focus:outline-none",
  "disabled:opacity-50",
])

const inputWrapperVariants = cva([
  "flex items-center gap-1.5",
  "h-control w-full rounded-md px-2",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "has-[input:disabled]:cursor-default has-[input:read-only]:cursor-default",
  "border border-surface-1",
  "focus-within:border-coval-border-active",
  "has-[:disabled]:opacity-50",
])

const innerInputVariants = cva([
  "h-full w-full py-1.5 bg-transparent",
  "text-sm text-coval-text placeholder:text-coval-text-muted",
  "outline-none",
  "[&[type=search]]:[-webkit-appearance:none] [&[type=search]::-webkit-search-cancel-button]:hidden",
])

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  onClear?: () => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      error,
      startAdornment,
      endAdornment,
      onClear,
      onChange,
      value,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(
      props.defaultValue ?? "",
    )
    const innerRef = React.useRef<HTMLInputElement>(null)

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const currentValue = value !== undefined ? value : internalValue
    const showClear = !!onClear && !!currentValue && !disabled

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value)
      }
      onChange?.(e)
    }

    const handleClear = () => {
      if (disabled) {
        return
      }
      if (value === undefined) {
        setInternalValue("")
      }
      onClear?.()
    }

    const resolvedEndAdornment = showClear ? (
      <button
        type="button"
        onClick={handleClear}
        aria-label="Clear"
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className="cursor-pointer disabled:cursor-default"
      >
        <X />
      </button>
    ) : (
      endAdornment
    )

    if (!startAdornment && !resolvedEndAdornment) {
      return (
        <input
          className={cn(
            inputVariants(),
            error && "bg-coval-danger-surface text-coval-danger-on-surface",
            className,
          )}
          ref={ref}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          {...props}
        />
      )
    }

    return (
      <div
        className={cn(
          inputWrapperVariants(),
          error && "bg-coval-danger-surface",
          className,
        )}
        onClick={(event) => {
          // Don't steal focus from nested interactive adornments (the clear
          // button, menu triggers, etc.); only focus on bare-container clicks.
          const target = event.target as HTMLElement
          if (
            target.closest(
              "button,a,input,select,textarea,[role='button'],[role='menuitem']",
            )
          ) {
            return
          }
          innerRef.current?.focus()
        }}
      >
        {startAdornment && (
          <span className="flex shrink-0 items-center text-coval-text-muted [&>svg]:size-4">
            {startAdornment}
          </span>
        )}
        <input
          className={cn(
            innerInputVariants(),
            error && "text-coval-danger-on-surface",
          )}
          ref={setRefs}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          {...props}
        />
        {resolvedEndAdornment && (
          <span className="flex shrink-0 items-center text-coval-text-muted [&_svg]:size-3.5">
            {resolvedEndAdornment}
          </span>
        )}
      </div>
    )
  },
)

Input.displayName = "Input"

export { Input, inputVariants }
